import { cn } from "@/lib/utils";
import type { PayoutBreakdown } from "@/services/payout.service";
import { formatCurrency, formatNumber } from "@/utils/formatters";

/**
 * A payout amount with, underneath, how much of it came from Recharge
 * renewals. Nothing extra is printed when there was no recurring share or
 * when the payout predates the breakdown (no zeros, no "unavailable").
 */
export function PayoutAmount({
  amount,
  breakdown,
  className,
}: {
  amount: number;
  breakdown?: PayoutBreakdown | null;
  className?: string;
}) {
  const recurring = breakdown?.recurringAmount ?? 0;
  return (
    <span className="inline-flex flex-col">
      <span className={cn("whitespace-nowrap tabular-nums", className)}>{formatCurrency(amount)}</span>
      {recurring > 0 && (
        <span className="whitespace-nowrap text-xs tabular-nums text-muted-foreground">
          {formatCurrency(recurring)} recurring
        </span>
      )}
    </span>
  );
}

/**
 * The two lines of a payout's make-up for a detail or invoice view. Renders
 * nothing when the payout has no breakdown.
 */
export function PayoutBreakdownRows({
  breakdown,
  rowClassName = "flex justify-between items-center mb-2",
}: {
  breakdown?: PayoutBreakdown | null;
  rowClassName?: string;
}) {
  if (!breakdown) return null;
  const renewals = breakdown.renewalCount;
  return (
    <>
      <div className={rowClassName}>
        <p className="text-sm text-muted-foreground">One-time commissions</p>
        <p className="font-medium tabular-nums">{formatCurrency(breakdown.oneTimeAmount)}</p>
      </div>
      <div className={rowClassName}>
        <p className="text-sm text-muted-foreground">
          Recurring commissions
          {renewals > 0 && (
            <span className="ml-1 text-xs">
              ({formatNumber(renewals)} {renewals === 1 ? "renewal" : "renewals"})
            </span>
          )}
        </p>
        <p className="font-medium tabular-nums">{formatCurrency(breakdown.recurringAmount)}</p>
      </div>
    </>
  );
}
